<template>
  <div class="layout">
    <TopNav />
    <div class="layout__body">
      <SideNav />
      <main v-if="admin_datos.superadmin" class="layout__main">
        <section class="card">
          <header class="card__header">
            <div class="card__headline">
              <h1 class="card__title">Gestión de administradores de propiedades</h1>
              <p class="card__subtitle">
                Administra cuentas, credenciales y acceso de los administradores de propiedades dentro de Jobbing.
              </p>
            </div>
            <button class="btn btn--primary" type="button" @click="toggleAddForm">
              {{ showAddForm ? 'Cerrar formulario' : 'Agregar administrador' }}
            </button>
          </header>

          <transition name="fade">
            <form v-if="showAddForm" class="form" @submit.prevent="addPropertyManager">
              <div class="form__grid">
                <label class="form__control">
                  <span>Nombre</span>
                  <input v-model="newManager.nombre" type="text" required />
                </label>
                <label class="form__control">
                  <span>Email</span>
                  <input v-model="newManager.email" type="email" required />
                </label>
                <label class="form__control">
                  <span>Contraseña</span>
                  <input v-model="newManager.password" type="password" required />
                </label>
                <label class="form__control">
                  <span>Teléfono</span>
                  <input v-model="newManager.telefono" type="text" />
                </label>
                <label class="form__control">
                  <span>Empresa de administración</span>
                  <select v-model="newManager.empresa_id" required>
                    <option disabled value="">Seleccione una empresa</option>
                    <option v-for="empresa in empresas" :key="empresa.id" :value="empresa.id">
                      {{ empresa.nombre }}
                    </option>
                  </select>
                </label>
              </div>
              <div class="form__actions">
                <button type="submit" class="btn btn--primary">Guardar</button>
                <button type="button" class="btn btn--ghost" @click="toggleAddForm">Cancelar</button>
              </div>
            </form>
          </transition>
        </section>

        <section class="table-wrapper" v-if="loaded">
          <table class="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nombre</th>
                <th>Email</th>
                <th>Teléfono</th>
                <th>Empresa</th>
                <th class="table__actions">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(manager, index) in propertyManagers" :key="manager.id">
                <td>{{ manager.id }}</td>
                <td>
                  <span v-if="!editing[index]">{{ manager.nombre }}</span>
                  <input v-else v-model="manager.nombre" />
                </td>
                <td>
                  <span v-if="!editing[index]">{{ manager.email }}</span>
                  <input v-else v-model="manager.email" />
                </td>
                <td>
                  <span v-if="!editing[index]">{{ manager.telefono }}</span>
                  <input v-else v-model="manager.telefono" />
                </td>
                <td>
                  <span v-if="!editing[index]">{{ manager.empresa_nombre }}</span>
                  <select v-else v-model="manager.empresa_id">
                    <option v-for="empresa in empresas" :key="empresa.id" :value="empresa.id">
                      {{ empresa.nombre }}
                    </option>
                  </select>
                </td>
                <td>
                  <div class="action-group">
                    <button
                      v-if="!editing[index]"
                      class="btn btn--small"
                      type="button"
                      @click="startEditing(index)"
                    >
                      Editar
                    </button>
                    <button
                      v-else
                      class="btn btn--small btn--primary"
                      type="button"
                      @click="saveChanges(manager, index)"
                    >
                      Guardar
                    </button>
                    <button
                      v-if="editing[index]"
                      class="btn btn--small btn--ghost"
                      type="button"
                      @click="cancelEditing(index)"
                    >
                      Cancelar
                    </button>
                    <button
                      class="btn btn--small btn--danger"
                      type="button"
                      @click="deletePropertyManager(manager.id)"
                    >
                      Eliminar
                    </button>
                    <button
                      class="btn btn--small btn--secondary"
                      type="button"
                      :disabled="resetting[manager.id]"
                      @click="resetPassword(manager)"
                    >
                      <span v-if="resetting[manager.id]" class="spinner" aria-hidden="true"></span>
                      <span>{{ resetting[manager.id] ? 'Restableciendo…' : 'Restablecer contraseña' }}</span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </section>
        <div v-else class="loading-state">Cargando administradores de propiedades…</div>
      </main>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import SideNav from '@/components/SideNav.vue';
import TopNav from '@/components/TopNav.vue';

export default {
  name: 'PropertyManagerManagement',
  components: {
    SideNav,
    TopNav,
  },
  data() {
    return {
      loaded: false,
      propertyManagers: [],
      empresas: [],
      editing: [],
      showAddForm: false,
      resetting: {},
      newManager: {
        nombre: '',
        email: '',
        password: '',
        telefono: '',
        empresa_id: '',
      },
    };
  },
  computed: {
    ...mapGetters(['admin_datos']),
  },
  created() {
    this.fetchPropertyManagers();
    this.fetchEmpresas();
  },
  mounted() {
    if (!this.admin_datos.superadmin) {
      this.$router.push('/');
    }
  },
  methods: {
    toggleAddForm() {
      this.showAddForm = !this.showAddForm;
      if (!this.showAddForm) {
        this.newManager = {
          nombre: '',
          email: '',
          password: '',
          telefono: '',
          empresa_id: '',
        };
      }
    },
    fetchPropertyManagers() {
      this.loaded = false;
      axios
        .get(window.hostname + 'api/superadmin/get_property_managers')
        .then((response) => {
          this.loaded = true;
          this.propertyManagers = response.data.property_managers;
          this.editing = this.propertyManagers.map(() => false);
        })
        .catch((error) => {
          this.loaded = true;
          console.error('Error al cargar administradores de propiedades', error);
        });
    },
    fetchEmpresas() {
      axios
        .get(window.hostname + 'api/superadmin/get_empresas_administracion')
        .then((response) => {
          this.empresas = response.data.empresas;
        })
        .catch((error) => {
          console.error('Error al cargar empresas de administración', error);
        });
    },
    startEditing(index) {
      this.$set(this.editing, index, true);
    },
    cancelEditing(index) {
      this.$set(this.editing, index, false);
      this.fetchPropertyManagers();
    },
    saveChanges(manager, index) {
      axios
        .post(window.hostname + 'api/superadmin/update_property_manager', manager)
        .then(() => {
          alert('Administrador de propiedades actualizado');
          this.$set(this.editing, index, false);
        })
        .catch((error) => {
          console.error('Error al actualizar administrador de propiedades', error);
        });
    },
    deletePropertyManager(managerId) {
      if (confirm('¿Estás seguro de que deseas eliminar este administrador de propiedades?')) {
        axios
          .post(window.hostname + 'api/superadmin/delete_property_manager', { id: managerId })
          .then(() => {
            alert('Administrador de propiedades eliminado');
            this.fetchPropertyManagers();
          })
          .catch((error) => {
            console.error('Error al eliminar administrador de propiedades', error);
          });
      }
    },
    addPropertyManager() {
      axios
        .post(window.hostname + 'api/superadmin/add_property_manager', this.newManager)
        .then(() => {
          alert('Administrador de propiedades agregado');
          this.toggleAddForm();
          this.fetchPropertyManagers();
        })
        .catch((error) => {
          console.error('Error al agregar administrador de propiedades', error);
        });
    },
    resetPassword(manager) {
      if (this.resetting[manager.id]) {
        return;
      }

      if (!confirm(`¿Deseas restablecer la contraseña de ${manager.nombre || manager.email}?`)) {
        return;
      }

      this.$set(this.resetting, manager.id, true);

      axios
        .post(window.hostname + 'api/superadmin/reset_property_manager_password', { id: manager.id })
        .then((response) => {
          const apiMessage = response && response.data ? response.data.message : null;
          const fallbackMessage = `Se envió una nueva contraseña temporal al correo ${manager.email}. Pídele que revise https://admin.jobbing.cl.`;
          alert(apiMessage || fallbackMessage);
        })
        .catch((error) => {
          const apiMessage = error && error.response && error.response.data ? error.response.data.message : null;
          const fallbackMessage = 'No se pudo restablecer la contraseña. Intenta nuevamente más tarde.';
          alert(apiMessage || fallbackMessage);
        })
        .finally(() => {
          this.$set(this.resetting, manager.id, false);
        });
    },
  },
};
</script>

<style scoped>
.layout {
  min-height: 100vh;
  background-color: #f2f7fb;
  text-align: left;
}

.layout__main {
  flex: 1;
  padding: 96px 40px 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-left: 260px;
}

.layout__body {
  display: flex;
  width: 100%;
}

.layout__body > :first-child {
  flex-shrink: 0;
}

.card {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  padding: 24px;
}

.card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.card__headline {
  max-width: 560px;
}

.card__title {
  margin: 0 0 4px;
  font-size: 1.75rem;
  font-weight: 600;
  color: #1f2937;
}

.card__subtitle {
  margin: 0;
  color: #4b5563;
}

.form {
  margin-top: 24px;
  border-top: 1px solid #e5e7eb;
  padding-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}

.form__control {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-weight: 600;
  color: #1f2937;
}

.form__control input,
.form__control select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.95rem;
}

.form__actions {
  display: flex;
  gap: 12px;
}

.table-wrapper {
  flex: 1;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  padding: 24px;
  overflow-x: auto;
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table th,
.table td {
  text-align: left;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
}

.table th {
  background: #0f172a;
  color: #ffffff;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 1;
}

.table td input,
.table td select {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.95rem;
}

.table__actions {
  width: 320px;
}

.action-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.btn {
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.btn--primary {
  background: #2563eb;
  color: #ffffff;
}

.btn--primary:hover:not(:disabled) {
  background: #1d4ed8;
}

.btn--secondary {
  background: #0f172a;
  color: #ffffff;
}

.btn--secondary:hover:not(:disabled) {
  background: #1e293b;
}

.btn--ghost {
  background: transparent;
  color: #1f2937;
  border: 1px solid #d1d5db;
}

.btn--ghost:hover:not(:disabled) {
  background: #f3f4f6;
}

.btn--danger {
  background: #dc2626;
  color: #ffffff;
}

.btn--danger:hover:not(:disabled) {
  background: #b91c1c;
}

.btn--small {
  padding: 8px 12px;
  font-size: 0.875rem;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #ffffff;
  border-radius: 50%;
  display: inline-block;
  animation: spin 0.8s linear infinite;
}

.loading-state {
  padding: 120px 0;
  text-align: center;
  color: #4b5563;
  font-weight: 600;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1024px) {
  .table__actions {
    width: auto;
  }
}

@media (max-width: 1024px) {
  .layout__main {
    padding: 88px 24px 32px;
    margin-left: 240px;
  }
}

@media (max-width: 768px) {
  .layout__main {
    margin-left: 0;
    padding: 80px 16px 24px;
  }

  .card__header {
    flex-direction: column;
    align-items: stretch;
  }

  .form__actions {
    flex-direction: column;
    align-items: stretch;
  }

  .action-group {
    flex-direction: column;
    align-items: stretch;
  }

  .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
